import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Car, Plus, Pencil, Trash2, Search, AlertTriangle, Eye, Calendar, Shield, FileText, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const CarsPage = () => {
  const { token, isAdmin } = useAuth();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isRenewDialogOpen, setIsRenewDialogOpen] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [renewField, setRenewField] = useState('');
  const [renewDate, setRenewDate] = useState('');
  const [formData, setFormData] = useState({
    car_type: '',
    color: '',
    model: '',
    year: new Date().getFullYear(),
    plate_number_ar: '',
    plate_number_en: '',
    serial_number: '',
    chassis_number: '',
    registration_expiry: '',
    insurance_expiry: '',
    inspection_expiry: '',
    insurance_type: '',
    insurance_company: '',
    notes: ''
  });

  const fetchCars = async () => {
    try {
      const response = await axios.get(`${API}/cars`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCars(response.data);
    } catch (error) {
      toast.error('خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCar) {
        await axios.put(`${API}/cars/${selectedCar.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('تم تحديث السيارة بنجاح');
      } else {
        await axios.post(`${API}/cars`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('تم إضافة السيارة بنجاح');
      }
      setIsDialogOpen(false);
      fetchCars();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/cars/${selectedCar.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حذف السيارة بنجاح');
      setIsDeleteDialogOpen(false);
      fetchCars();
    } catch (error) {
      toast.error('خطأ في حذف السيارة');
    }
  };

  const openRenewDialog = (car, field, fieldLabel) => {
    setSelectedCar(car);
    setRenewField(field);
    setRenewDate('');
    setIsRenewDialogOpen(true);
  };

  const handleRenew = async () => {
    if (!renewDate) {
      toast.error('يرجى إدخال التاريخ الجديد');
      return;
    }
    try {
      const updateData = { [renewField]: renewDate };
      await axios.put(`${API}/cars/${selectedCar.id}`, 
        { ...selectedCar, ...updateData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('تم التجديد بنجاح');
      setIsRenewDialogOpen(false);
      fetchCars();
    } catch (error) {
      toast.error('خطأ في التجديد');
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      registration_expiry: 'الاستمارة',
      insurance_expiry: 'التأمين',
      inspection_expiry: 'الفحص'
    };
    return labels[field] || field;
  };

  const resetForm = () => {
    setFormData({
      car_type: '',
      color: '',
      model: '',
      year: new Date().getFullYear(),
      plate_number_ar: '',
      plate_number_en: '',
      serial_number: '',
      chassis_number: '',
      registration_expiry: '',
      insurance_expiry: '',
      inspection_expiry: '',
      insurance_type: '',
      insurance_company: '',
      notes: ''
    });
    setSelectedCar(null);
  };

  const openEditDialog = (car) => {
    setSelectedCar(car);
    setFormData({
      car_type: car.car_type,
      color: car.color,
      model: car.model,
      year: car.year,
      plate_number_ar: car.plate_number_ar || car.plate_number || '',
      plate_number_en: car.plate_number_en || '',
      serial_number: car.serial_number || '',
      chassis_number: car.chassis_number || '',
      registration_expiry: car.registration_expiry,
      insurance_expiry: car.insurance_expiry,
      inspection_expiry: car.inspection_expiry,
      insurance_type: car.insurance_type || '',
      insurance_company: car.insurance_company || '',
      notes: car.notes || ''
    });
    setIsDialogOpen(true);
  };

  const openViewDialog = (car) => {
    setSelectedCar(car);
    setIsViewDialogOpen(true);
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const expiry = new Date(dateStr);
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diff <= 30 && diff >= 0;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const expiry = new Date(dateStr);
    return expiry < today;
  };

  const filteredCars = cars.filter(car =>
    (car.car_type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (car.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (car.plate_number_ar || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (car.plate_number_en || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cars-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold" style={{ color: '#1a365d' }}>السيارات</h1>
          <p className="text-slate-500 mt-1">إدارة جميع السيارات والمركبات</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="text-white"
            style={{ background: '#1a365d' }}
            data-testid="add-car-btn"
          >
            <Plus className="w-4 h-4 ml-2" />
            إضافة سيارة
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="البحث عن سيارة..."
          className="pr-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-cars"
        />
      </div>

      {/* Cars Table */}
      <Card className="border-0 shadow-sm" data-testid="cars-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">السيارة</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">رقم اللوحة</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">الاستمارة</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">التأمين</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">الفحص</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600 w-32">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredCars.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-400">
                      <Car className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>لا توجد سيارات</p>
                    </td>
                  </tr>
                ) : (
                  filteredCars.map((car) => (
                    <tr key={car.id} className="border-b hover:bg-slate-50 transition-colors" data-testid={`car-row-${car.id}`}>
                      <td className="py-3 pr-4 pl-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Car className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="text-right">
                            <p className="font-medium" style={{ color: '#1a365d' }}>{car.car_type} {car.model}</p>
                            <p className="text-sm text-slate-500">{car.color} - {car.year}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center">
                          <span className="font-medium">{car.plate_number_ar || car.plate_number || '-'}</span>
                          {car.plate_number_en && (
                            <span className="text-sm text-slate-500 number">{car.plate_number_en}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center gap-2 justify-center flex-wrap">
                          <span className="number text-sm">{formatDate(car.registration_expiry)}</span>
                          {isExpiringSoon(car.registration_expiry) && !isExpired(car.registration_expiry) && (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          )}
                          {isExpired(car.registration_expiry) && (
                            <>
                              <Badge variant="destructive" className="text-xs">منتهية</Badge>
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                  onClick={() => openRenewDialog(car, 'registration_expiry', 'الاستمارة')}
                                  data-testid={`renew-registration-${car.id}`}
                                >
                                  <RefreshCw className="w-3 h-3 ml-1" />
                                  تجديد
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center gap-2 justify-center flex-wrap">
                          <span className="number text-sm">{formatDate(car.insurance_expiry)}</span>
                          {isExpiringSoon(car.insurance_expiry) && !isExpired(car.insurance_expiry) && (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          )}
                          {isExpired(car.insurance_expiry) && (
                            <>
                              <Badge variant="destructive" className="text-xs">منتهي</Badge>
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                  onClick={() => openRenewDialog(car, 'insurance_expiry', 'التأمين')}
                                  data-testid={`renew-insurance-${car.id}`}
                                >
                                  <RefreshCw className="w-3 h-3 ml-1" />
                                  تجديد
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center gap-2 justify-center flex-wrap">
                          <span className="number text-sm">{formatDate(car.inspection_expiry)}</span>
                          {isExpiringSoon(car.inspection_expiry) && !isExpired(car.inspection_expiry) && (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          )}
                          {isExpired(car.inspection_expiry) && (
                            <>
                              <Badge variant="destructive" className="text-xs">منتهي</Badge>
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                  onClick={() => openRenewDialog(car, 'inspection_expiry', 'الفحص')}
                                  data-testid={`renew-inspection-${car.id}`}
                                >
                                  <RefreshCw className="w-3 h-3 ml-1" />
                                  تجديد
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-blue-600 hover:text-blue-700"
                            onClick={() => openViewDialog(car)}
                            data-testid={`view-car-${car.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditDialog(car)}
                                data-testid={`edit-car-${car.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-600"
                                onClick={() => { setSelectedCar(car); setIsDeleteDialogOpen(true); }}
                                data-testid={`delete-car-${car.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl" dir="rtl">
          <DialogHeader>
            <DialogTitle>{selectedCar ? 'تعديل السيارة' : 'إضافة سيارة جديدة'}</DialogTitle>
            <DialogDescription>أدخل بيانات السيارة بالكامل</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label>نوع السيارة</Label>
                <Input
                  value={formData.car_type}
                  onChange={(e) => setFormData({ ...formData, car_type: e.target.value })}
                  placeholder="تويوتا، مرسيدس..."
                  required
                  data-testid="car-type-input"
                />
              </div>
              <div className="space-y-2">
                <Label>الموديل</Label>
                <Input
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="كامري، S-Class..."
                  required
                  data-testid="car-model-input"
                />
              </div>
              <div className="space-y-2">
                <Label>اللون</Label>
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="أبيض، أسود..."
                  required
                  data-testid="car-color-input"
                />
              </div>
              <div className="space-y-2">
                <Label>سنة الصنع</Label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  required
                  data-testid="car-year-input"
                />
              </div>
              <div className="space-y-2">
                <Label>رقم اللوحة (عربي)</Label>
                <Input
                  value={formData.plate_number_ar}
                  onChange={(e) => setFormData({ ...formData, plate_number_ar: e.target.value })}
                  placeholder="أ ب ج ١٢٣٤"
                  data-testid="car-plate-ar-input"
                />
              </div>
              <div className="space-y-2">
                <Label>رقم اللوحة (إنجليزي)</Label>
                <Input
                  value={formData.plate_number_en}
                  onChange={(e) => setFormData({ ...formData, plate_number_en: e.target.value })}
                  placeholder="ABC 1234"
                  data-testid="car-plate-en-input"
                />
              </div>
              <div className="space-y-2">
                <Label>الرقم التسلسلي</Label>
                <Input
                  value={formData.serial_number}
                  onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                  data-testid="car-serial-input"
                />
              </div>
              <div className="space-y-2">
                <Label>رقم الشاسيه</Label>
                <Input
                  value={formData.chassis_number}
                  onChange={(e) => setFormData({ ...formData, chassis_number: e.target.value })}
                  data-testid="car-chassis-input"
                />
              </div>
              <div className="space-y-2">
                <Label>نوع التأمين</Label>
                <Input
                  value={formData.insurance_type}
                  onChange={(e) => setFormData({ ...formData, insurance_type: e.target.value })}
                  placeholder="شامل، ضد الغير..."
                  required
                  data-testid="car-insurance-type-input"
                />
              </div>
              <div className="space-y-2">
                <Label>شركة التأمين</Label>
                <Input
                  value={formData.insurance_company}
                  onChange={(e) => setFormData({ ...formData, insurance_company: e.target.value })}
                  placeholder="التعاونية، بوبا..."
                  required
                  data-testid="car-insurance-company-input"
                />
              </div>
              <div className="space-y-2">
                <Label>انتهاء الاستمارة</Label>
                <Input
                  type="date"
                  value={formData.registration_expiry}
                  onChange={(e) => setFormData({ ...formData, registration_expiry: e.target.value })}
                  required
                  data-testid="car-registration-input"
                />
              </div>
              <div className="space-y-2">
                <Label>انتهاء التأمين</Label>
                <Input
                  type="date"
                  value={formData.insurance_expiry}
                  onChange={(e) => setFormData({ ...formData, insurance_expiry: e.target.value })}
                  required
                  data-testid="car-insurance-input"
                />
              </div>
              <div className="space-y-2">
                <Label>انتهاء الفحص</Label>
                <Input
                  type="date"
                  value={formData.inspection_expiry}
                  onChange={(e) => setFormData({ ...formData, inspection_expiry: e.target.value })}
                  required
                  data-testid="car-inspection-input"
                />
              </div>
              <div className="space-y-2 col-span-4">
                <Label>ملاحظات</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="ملاحظات إضافية..."
                  data-testid="car-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                إلغاء
              </Button>
              <Button type="submit" className="text-white" style={{ background: '#1a365d' }} data-testid="save-car-btn">
                {selectedCar ? 'حفظ التعديلات' : 'إضافة السيارة'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="w-5 h-5" style={{ color: '#D4AF37' }} />
              تفاصيل السيارة
            </DialogTitle>
          </DialogHeader>
          {selectedCar && (
            <div className="space-y-6">
              {/* Car Header */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Car className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold" style={{ color: '#1a365d' }}>
                    {selectedCar.car_type} {selectedCar.model}
                  </h3>
                  <p className="text-slate-500">{selectedCar.color} - {selectedCar.year}</p>
                </div>
              </div>

              {/* Plate Numbers */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <p className="text-sm text-slate-500 mb-1">رقم اللوحة (عربي)</p>
                  <p className="text-lg font-bold" style={{ color: '#1a365d' }}>
                    {selectedCar.plate_number_ar || selectedCar.plate_number || '-'}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <p className="text-sm text-slate-500 mb-1">رقم اللوحة (إنجليزي)</p>
                  <p className="text-lg font-bold number" style={{ color: '#1a365d' }}>
                    {selectedCar.plate_number_en || '-'}
                  </p>
                </div>
              </div>

              {/* Dates */}
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2" style={{ color: '#1a365d' }}>
                  <Calendar className="w-4 h-4" style={{ color: '#D4AF37' }} />
                  التواريخ
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className={`p-3 rounded-xl border ${isExpired(selectedCar.registration_expiry) ? 'bg-red-50 border-red-200' : isExpiringSoon(selectedCar.registration_expiry) ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                    <p className="text-xs text-slate-500">الاستمارة</p>
                    <p className="font-bold number">{selectedCar.registration_expiry}</p>
                    {isExpired(selectedCar.registration_expiry) && <Badge variant="destructive" className="mt-1 text-xs">منتهية</Badge>}
                  </div>
                  <div className={`p-3 rounded-xl border ${isExpired(selectedCar.insurance_expiry) ? 'bg-red-50 border-red-200' : isExpiringSoon(selectedCar.insurance_expiry) ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                    <p className="text-xs text-slate-500">التأمين</p>
                    <p className="font-bold number">{selectedCar.insurance_expiry}</p>
                    {isExpired(selectedCar.insurance_expiry) && <Badge variant="destructive" className="mt-1 text-xs">منتهي</Badge>}
                  </div>
                  <div className={`p-3 rounded-xl border ${isExpired(selectedCar.inspection_expiry) ? 'bg-red-50 border-red-200' : isExpiringSoon(selectedCar.inspection_expiry) ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                    <p className="text-xs text-slate-500">الفحص</p>
                    <p className="font-bold number">{selectedCar.inspection_expiry}</p>
                    {isExpired(selectedCar.inspection_expiry) && <Badge variant="destructive" className="mt-1 text-xs">منتهي</Badge>}
                  </div>
                </div>
              </div>

              {/* Other Details */}
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2" style={{ color: '#1a365d' }}>
                  <FileText className="w-4 h-4" style={{ color: '#D4AF37' }} />
                  بيانات أخرى
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">الرقم التسلسلي</p>
                    <p className="font-medium number">{selectedCar.serial_number || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">رقم الشاسيه</p>
                    <p className="font-medium number">{selectedCar.chassis_number || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Insurance */}
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2" style={{ color: '#1a365d' }}>
                  <Shield className="w-4 h-4" style={{ color: '#D4AF37' }} />
                  التأمين
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">نوع التأمين</p>
                    <p className="font-medium">{selectedCar.insurance_type || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">شركة التأمين</p>
                    <p className="font-medium">{selectedCar.insurance_company || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedCar.notes && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <p className="text-xs text-slate-500 mb-1">ملاحظات</p>
                  <p className="text-slate-700">{selectedCar.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              إغلاق
            </Button>
            {isAdmin && (
              <Button 
                className="text-white" 
                style={{ background: '#1a365d' }}
                onClick={() => { setIsViewDialogOpen(false); openEditDialog(selectedCar); }}
              >
                تعديل
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader className="text-right">
            <AlertDialogTitle className="text-right">تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              هل أنت متأكد من حذف هذه السيارة؟ لا يمكن التراجع عن هذا الإجراء.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-car"
            >
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Renew Dialog */}
      <Dialog open={isRenewDialogOpen} onOpenChange={setIsRenewDialogOpen}>
        <DialogContent className="max-w-sm" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" style={{ color: '#D4AF37' }} />
              تجديد {getFieldLabel(renewField)}
            </DialogTitle>
            <DialogDescription>
              أدخل تاريخ الانتهاء الجديد لـ{getFieldLabel(renewField)}
            </DialogDescription>
          </DialogHeader>
          {selectedCar && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">السيارة</p>
                <p className="font-medium" style={{ color: '#1a365d' }}>
                  {selectedCar.car_type} {selectedCar.model} - {selectedCar.plate_number_ar || selectedCar.plate_number}
                </p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <p className="text-sm text-slate-500">التاريخ الحالي (منتهي)</p>
                <p className="font-medium text-red-600 number">{selectedCar[renewField]}</p>
              </div>
              <div className="space-y-2">
                <Label>تاريخ الانتهاء الجديد</Label>
                <Input
                  type="date"
                  value={renewDate}
                  onChange={(e) => setRenewDate(e.target.value)}
                  data-testid="renew-date-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsRenewDialogOpen(false)}>
              إلغاء
            </Button>
            <Button 
              onClick={handleRenew}
              className="text-white bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-renew-btn"
            >
              <RefreshCw className="w-4 h-4 ml-2" />
              تأكيد التجديد
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CarsPage;
